import { useEffect, useRef, useState } from 'react';
import { StatusChip, Swipe, cx, useReveal } from '../../ui';
import type { Sector } from './sectors';
import { CHAPTERS, type Chapter } from './chapters';
import { RecordCard, Scene } from './parts';
import { storyStore } from './store';

function ChapterText({ c, i, s, as: H = 'h3' }: { c: Chapter; i: number; s: Sector; as?: 'h3' }) {
  const [pre, sw, post] = c.title(s);
  return (
    <>
      <div className="vc-ch-n">
        <span className="vc-ch-num font-mono">{String(i + 1).padStart(2, '0')}</span>
        <span className="vc-ch-stage">{c.stage}</span>
        <span className="vc-ch-place">{c.place(s)}</span>
      </div>
      <H className="vc-ch-t">
        {pre}
        <Swipe>{sw}</Swipe>
        {post}
      </H>
      <p className="vc-ch-body">{c.body(s)}</p>
      <div className="vc-gain">
        <span>{c.gain(s)}</span>
        {c.status !== 'live' && <StatusChip status={c.status} />}
      </div>
      {c.also && (() => {
        const a = c.also(s);
        return (
          <p className="vc-also">
            <span className="vc-also-k">Also</span>
            <span>
              {a.t} {a.status && a.status !== 'live' && <StatusChip status={a.status} />}
            </span>
          </p>
        );
      })()}
    </>
  );
}

export function Story({ s }: { s: Sector }) {
  const [active, setActive] = useState(0);
  const [shown, setShown] = useState(0);
  const [wipe, setWipe] = useState(0);
  const sectionRef = useRef<HTMLElement>(null);
  const pinRefs = useRef<(HTMLElement | null)[]>([]);
  const stackRefs = useRef<(HTMLElement | null)[]>([]);
  const stackRoot = useReveal<HTMLDivElement>();

  // Which chapter is in the middle of the screen? Plain scroll position, no scroll-jacking.
  useEffect(() => {
    let raf = 0;
    const measure = () => {
      raf = 0;
      const sec = sectionRef.current;
      if (!sec) return;
      const vh = window.innerHeight;
      const r = sec.getBoundingClientRect();
      const pinned = (pinRefs.current[0]?.offsetParent ?? null) !== null;
      const els = pinned ? pinRefs.current : stackRefs.current;
      let a = 0;
      els.forEach((el, k) => {
        if (el && el.getBoundingClientRect().top < vh * 0.55) a = k;
      });
      setActive(a);
      storyStore.set({
        active: a,
        progress: Math.min(1, Math.max(0, (vh * 0.55 - r.top) / r.height)),
        inStory: r.top < vh * 0.55 && r.bottom > vh * 0.45,
      });
    };
    const on = () => {
      if (!raf) raf = requestAnimationFrame(measure);
    };
    measure();
    window.addEventListener('scroll', on, { passive: true });
    window.addEventListener('resize', on);
    return () => {
      window.removeEventListener('scroll', on);
      window.removeEventListener('resize', on);
      cancelAnimationFrame(raf);
      storyStore.set({ inStory: false });
    };
  }, []);

  // Cut, don't fade: the gradient wipes across the scene and the new screen is underneath.
  useEffect(() => {
    if (active === shown) return;
    setWipe((w) => w + 1);
    const t = window.setTimeout(() => setShown(active), 200);
    return () => window.clearTimeout(t);
  }, [active, shown]);

  const jump = (k: number) => {
    const el = pinRefs.current[k];
    if (!el) return;
    const y = el.getBoundingClientRect().top + window.scrollY - window.innerHeight * 0.3;
    window.scrollTo({ top: y, behavior: 'smooth' });
  };

  return (
    <section className="vc-story" id="story" ref={sectionRef} aria-labelledby="vc-story-title">
      <div className="wrap">
        <div className="vc-story-intro">
          <h2 id="vc-story-title" className="t-h2">
            Its whole life, on <Swipe>one record</Swipe>.
          </h2>
          <p className="t-lede mt-5 max-w-[52ch]">
            Every scan, signature and claim is added to the unit&rsquo;s record, from the factory to the last owner.
          </p>
          <a href="#expert" className="vc-skip">
            Skip the story
          </a>
        </div>

        {/* Desktop with motion: chapters scroll, the record stays pinned */}
        <div className="vc-sticky">
          <div className="vc-chapters">
            {CHAPTERS.map((c, k) => (
              <article
                key={c.key}
                ref={(el) => {
                  pinRefs.current[k] = el;
                }}
                className={cx('vc-ch', k === active && 'is-active', k <= active && 'is-seen')}
              >
                <ChapterText c={c} i={k} s={s} />
              </article>
            ))}
          </div>
          <div className="vc-pin" aria-hidden="true">
            <div className="vc-pin-inner">
              <div className="vc-stepper">
                {CHAPTERS.map((c, k) => (
                  <button
                    key={c.key}
                    type="button"
                    tabIndex={-1}
                    className={cx('vc-step', k < active && 'is-done', k === active && 'is-active')}
                    onClick={() => jump(k)}
                  >
                    {c.stage}
                  </button>
                ))}
              </div>
              <div className="vc-duo">
                <RecordCard s={s} i={active} />
                <div className="vc-scene-box">
                  <Scene s={s} i={shown} />
                  {wipe > 0 && <span key={wipe} className="vc-wipe" />}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Phones, tablets and reduced motion: each chapter carries its own record state */}
        <div className="vc-stack" ref={stackRoot}>
          {CHAPTERS.map((c, k) => (
            <article
              key={c.key}
              ref={(el) => {
                stackRefs.current[k] = el;
              }}
              className="vc-sch"
              data-reveal
            >
              <div className="vc-sch-text">
                <ChapterText c={c} i={k} s={s} />
              </div>
              <div className="vc-sch-vis">
                <RecordCard s={s} i={k} compact />
                {(() => {
                  const e = c.entries(s)[0];
                  return e ? (
                    <div className="vc-sch-entry">
                      <span className="vc-sch-entry-l">Added to the record</span>
                      <span className="vc-sch-entry-t">{e.title}</span>
                      <span className="vc-sch-entry-m font-mono">
                        {e.date} · {e.meta}
                      </span>
                    </div>
                  ) : null;
                })()}
                <div className="vc-sch-scene">
                  <Scene s={s} i={k} />
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
